import React, {forwardRef, ReactElement, ReactNode, Ref, useEffect, useRef, useState} from "react";
import {
  Button,
  ButtonBase,
  ButtonProps,
  Card,
  IconButton,
  InputBase,
  Slider,
  SvgIcon,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "@/shared/StyledComponents";
import {DIVIDER, PAGE_FRAGMENT_HALF_WIDTH, PD_MD, PD_SM, SZ_SM} from "@/shared/dimens";
import {
  ArrowBackIosNewOutlined, CommentOutlined,
  HelpOutlineOutlined, InsertCommentOutlined,
  LabelOutlined,
  OpenInFullOutlined,
  RestartAltOutlined,
  SettingsOutlined
} from "@mui/icons-material";
import {
  CommentElement,
  ShapeCanvasElement,
  SidebarContext,
  SidebarRenderer,
  WORKBENCH_TOGGLE_KTM,
  WORKBENCH_TOOL_KTM,
  WorkbenchToggle,
  WorkbenchTool
} from "./types";
import {colorHighlightAlt, mediumGray} from "@/shared/colors";
import {LabelsSidebarRenderer} from "./sidebar/LabelsSidebarRenderer";
import {HelpSidebarRenderer} from "./sidebar/HelpSidebarRenderer";
import {LabelerTask, LabelerTaskCase} from "@/fyneai-shared/types";
import {CommentsSidebarRenderer} from "./sidebar/CommentsSidebarRenderer";


export enum SidebarItemId {
  LABELS = "labels",
  COMMENTS = "comments",
  HELP = "help",
}

export type SidebarItem = {
  id: string,
  text: string,
  iconType: typeof SvgIcon,
  renderer?: SidebarRenderer,
}

const WORKBENCH_SIDEBAR_ITEMS: SidebarItem[] = [
  {
    id: SidebarItemId.LABELS,
    text: "Labels",
    iconType: LabelOutlined,
    renderer: new LabelsSidebarRenderer(),
  },
  {
    id: SidebarItemId.COMMENTS,
    text: "Comments",
    iconType: CommentOutlined,
    renderer: new CommentsSidebarRenderer(),
  },
  // {
  //   id: SidebarItemId.HELP,
  //   text: "Help",
  //   iconType: HelpOutlineOutlined,
  //   renderer: new HelpSidebarRenderer(),
  // },
];

function SidebarItemView({item, context}: { item: SidebarItem, context?: SidebarContext }): ReactElement {
  const theme = useTheme();
  return <div style={{
    display: "flex",
    flexDirection: "column",
    width: PAGE_FRAGMENT_HALF_WIDTH,
    flexShrink: 0,
    height: "100%",
    overflow:"hidden",
    background: theme.palette.background.default,
    borderLeft: DIVIDER,
  }}>
    <StyledBoxRow style={{
      width: "100%",
      gap: 0,
      height: SZ_SM,
      padding: PD_SM,
      borderBottom: DIVIDER,
      alignItems: "center"
    }}>
      <Typography
        variant={"h6"}>
        {item.text}
      </Typography>
      {item.renderer?.renderToolbar?.(context) || <StyledSpan/>}
    </StyledBoxRow>
    <div style={{display: "flex", flexDirection: "column", flexBasis: 0, flexGrow: 1, overflowY:'scroll'}}>
      {item.renderer?.renderContent(context)}
    </div>
  </div>;
}

const DEFAULT_BRIGHTNESS = 128;
const DEFAULT_CONTRAST = 128;
const DEFAULT_SATURATION = 128;
const DEFAULT_ROTATION = 0;

function AdjustmentPanel({value, defaultValue, min = 0, max = 255, step = 1, onChange, anchorTop}: {
  value: number,
  defaultValue: number,
  min?: number,
  max?: number,
  step?: number,
  onChange: (value: number) => void,
  anchorTop: number,
}): ReactElement {
  const theme = useTheme();
  return <Card style={{
    position: "absolute",
    left: 48,
    top: anchorTop,
    zIndex: 10,
    width: 240,
    padding: PD_MD,
    display: "flex",
    flexDirection: "column",
    gap: PD_SM,
    boxShadow: theme.shadows[4],
  }}>
    <StyledBoxRow style={{alignItems: "center", gap: PD_SM}}>
      <SettingsOutlined style={{color: mediumGray, fontSize: 20}}/>
      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(_, v) => onChange(v as number)}
        style={{flexGrow: 1}}
        size="small"
      />
      <IconButton size="small" onClick={() => onChange(defaultValue)}>
        <RestartAltOutlined fontSize="small"/>
      </IconButton>
    </StyledBoxRow>
    <InputBase
      value={value}
      type="number"
      inputProps={{min, max, step}}
      onChange={(e) => {
        const v = parseInt(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }}
      style={{
        background: mediumGray + "22",
        borderRadius: 4,
        padding: `${PD_SM / 2}px ${PD_SM}px`,
        fontSize: 14,
      }}
    />
  </Card>;
}

export type WorkbenchTogglesState = Record<WorkbenchToggle, boolean>;

const ToolstripButton = forwardRef((props: ButtonProps & { selected?: boolean }, ref: Ref<HTMLButtonElement>) => {
  return <ButtonBase
    ref={ref}
    {...props}
    style={{minWidth: 0, width: 32, height: 32, background: props.selected ? colorHighlightAlt : null}}
  />;
});

export function WorkbenchContainer(
  {
    case_,
    selectedTool,
    setSelectedTool,
    onBack,
    children,
    brightness,
    onBrightnessChange,
    contrast,
    onContrastChange,
    saturation,
    onSaturationChange,
    rotation,
    onRotationChange,
    togglesState,
    onToggleStateChange,
    elements,
    onSelectElement,
    onDeleteElement,
    comments,
    onAddComment,
    onDeleteComment,
    onSelectComment,
  }: {
    case_: LabelerTaskCase,
    selectedTool: WorkbenchTool,
    setSelectedTool: (tool: WorkbenchTool) => void,
    onBack?: () => void,
    children: ReactNode,
    brightness?: number,
    onBrightnessChange?: (value: number) => void,
    contrast?: number,
    onContrastChange?: (value: number) => void,
    saturation?: number,
    onSaturationChange?: (value: number) => void,
    rotation?: number,
    onRotationChange?: (value: number) => void,
    togglesState: WorkbenchTogglesState,
    onToggleStateChange?: (key: WorkbenchToggle, value: boolean) => void,
    elements?: ShapeCanvasElement[],
    onSelectElement?: (id: string) => void,
    onDeleteElement?: (id: string) => void,
    comments?: CommentElement[],
    onAddComment?: (comment: CommentElement) => void,
    onDeleteComment?: (id: string) => void,
    onSelectComment?: (id: string) => void,
  }): ReactElement {
  const theme = useTheme();
  const tools = WORKBENCH_TOOL_KTM.values.filter(value => !value.metadata.taskFilter || value.metadata.taskFilter(case_.getTask()));
  const toggles = WORKBENCH_TOGGLE_KTM.values;
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [panelAnchorTop, setPanelAnchorTop] = useState(0);
  const [selectedSidebarItemId, setSelectedSidebarItemId] = useState<string>(SidebarItemId.LABELS);
  const sidebarContext: SidebarContext = {case_, elements, onSelectElement, onDeleteElement, comments, onAddComment, onDeleteComment, onSelectComment};
  useEffect(() => {
    const adjustmentKey = selectedTool === WorkbenchTool.BRIGHTNESS || selectedTool === WorkbenchTool.CONTRAST || selectedTool === WorkbenchTool.SATURATION || selectedTool === WorkbenchTool.ROTATE ? selectedTool : null;
    if (adjustmentKey) {
      const btn = buttonRefs.current.get(adjustmentKey);
      if (btn) {
        setPanelAnchorTop(btn.offsetTop);
      }
    }
  }, [selectedTool]);
  return <div
    style={{position: "relative", width: "100%", height: "100%", overflow: "hidden", flexGrow: 1, display: "flex"}}>
    <div style={{
      display: "flex",
      width: 40,
      gap: 4,
      padding: 4,
      flexDirection: "column",
      background: theme.palette.background.paper,
      borderRight: DIVIDER,
      position: "relative",
    }}>
      {Boolean(onBack)
        ? <Button startIcon={<ArrowBackIosNewOutlined/>} onClick={onBack}>Back</Button>
        : null}
      {tools.map(tool => <Tooltip key={tool.key} title={tool.metadata.text} placement="right">
        <ToolstripButton
          ref={(el: HTMLButtonElement) => {
            if (el) buttonRefs.current.set(tool.key, el);
          }}
          disabled={tool.metadata.disabled}
          onClick={() => setSelectedTool(tool.key as WorkbenchTool)}
          selected={selectedTool === tool.key}>
          <StyledBoxColumn style={{width: "100%", alignItems: "center", gap: 4}}>
            <tool.metadata.iconType/>
          </StyledBoxColumn>
        </ToolstripButton>
      </Tooltip>)}
      {selectedTool === WorkbenchTool.BRIGHTNESS && onBrightnessChange && (
        <AdjustmentPanel
          value={brightness ?? DEFAULT_BRIGHTNESS}
          defaultValue={DEFAULT_BRIGHTNESS}
          onChange={onBrightnessChange}
          anchorTop={panelAnchorTop}
        />
      )}
      {selectedTool === WorkbenchTool.CONTRAST && onContrastChange && (
        <AdjustmentPanel
          value={contrast ?? DEFAULT_CONTRAST}
          defaultValue={DEFAULT_CONTRAST}
          onChange={onContrastChange}
          anchorTop={panelAnchorTop}
        />
      )}
      {selectedTool === WorkbenchTool.SATURATION && onSaturationChange && (
        <AdjustmentPanel
          value={saturation ?? DEFAULT_SATURATION}
          defaultValue={DEFAULT_SATURATION}
          onChange={onSaturationChange}
          anchorTop={panelAnchorTop}
        />
      )}
      {selectedTool === WorkbenchTool.ROTATE && onRotationChange && (
        <AdjustmentPanel
          value={rotation ?? DEFAULT_ROTATION}
          defaultValue={DEFAULT_ROTATION}
          min={-180}
          max={180}
          step={10}
          onChange={onRotationChange}
          anchorTop={panelAnchorTop}
        />
      )}
      <div style={{borderTop: DIVIDER, margin: `${PD_SM}px 0`}}/>
      {toggles.map(toggle => <Tooltip key={toggle.key} title={toggle.metadata.text} placement="right">
        <ToolstripButton
          onClick={() => onToggleStateChange?.(toggle.key as WorkbenchToggle, !togglesState?.[toggle.key as WorkbenchToggle])}
          selected={togglesState?.[toggle.key as WorkbenchToggle]}>
          <StyledBoxColumn style={{width: "100%", alignItems: "center", gap: 4}}>
            <toggle.metadata.iconType/>
          </StyledBoxColumn>
        </ToolstripButton>
      </Tooltip>)}
    </div>
    <div style={{display: "flex", width: "100%", flexBasis: 0, flexGrow: 1}}>
      {children}
    </div>
    {selectedSidebarItemId
      ? <SidebarItemView
        key={`app-sidebar-${selectedSidebarItemId}`}
        item={WORKBENCH_SIDEBAR_ITEMS.find(item => item.id === selectedSidebarItemId)}
        context={sidebarContext}
      />
      : null}
    <StyledBoxColumn style={{
      width: 48,
      background: theme.palette.grey[100],
      borderLeft: DIVIDER,
      flexShrink: 0,
      padding: 4
    }}>
      {WORKBENCH_SIDEBAR_ITEMS.map(item => {
        const IconType = item.iconType;
        return <Tooltip title={item.text} placement={"left"}>
          <Button
            onClick={() => setSelectedSidebarItemId(item.id)}
            style={{
              padding: 4,
              minWidth: 0,
              width: 40,
              height: 40,
              background: item.id === selectedSidebarItemId ? colorHighlightAlt : null
            }}>
            <IconType style={{width: 20, height: 20}}/>
          </Button>
        </Tooltip>;
      })}
    </StyledBoxColumn>
  </div>;
}
